import { Fragment, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { date, dateTime } from '@/lib/format'
import {
  BILLING_KINDS,
  CONTRACT_YEARS,
  cellKey,
  dropProduct,
  gridToRates,
  missingCells,
  missingRates,
  productsOf,
  seedGrid,
  seedProduct,
} from '@/lib/commission'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Select, Textarea } from '@/components/ui/form'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  CommissionCell,
  CommissionPlan,
  CommissionPlanProduct,
  Paged,
  PaymentBasis,
  ProductListItem,
} from '@/types/api'

/**
 * What a grid is for, and therefore where it is saved and what shape it may take.
 *
 * A product's standard grid and a partner's are the same object - drafted, activated,
 * effective-dated, superseded - so they are the same screen too. What differs is the subject and
 * two rules that follow from it.
 */
export type PlanSubject =
  | { kind: 'reseller'; resellerId: string; downlineId: string | null }
  | { kind: 'product'; productId: string }

/** A standard grid is already about one product, so its cells carry none of their own. */
function allowsProductRows(subject: PlanSubject): boolean {
  return subject.kind === 'reseller'
}

/**
 * Only a partner's custom grid may go without a blanket row, because the product standards sit
 * underneath it. Everywhere else a missing blanket falls through to nothing.
 */
function blanketIsOptional(subject: PlanSubject): boolean {
  return subject.kind === 'reseller' && subject.downlineId === null
}

function listUrl(subject: PlanSubject): string {
  return subject.kind === 'reseller'
    ? `/resellers/${subject.resellerId}/commission-plans`
    : `/products/${subject.productId}/commission-plans`
}

/**
 * One subject's commission grids: the draft, the one in force, and what came before.
 *
 * Drafted, then activated, the same split as uploading a terms version and publishing it - a
 * half-built grid must never be the one a submit freezes. Activating is the Owner's alone,
 * because putting a rate in force decides how much money leaves the company for the next five
 * years.
 */
export function CommissionGrid({
  subject,
  countryId,
  plans,
  onChanged,
  title,
  emptyTitle,
  emptyHint,
  editorDescription,
  footnote,
}: {
  subject: PlanSubject
  countryId: string | undefined
  /** Already filtered to this subject and, for a reseller, this tier. */
  plans: readonly CommissionPlan[]
  onChanged: () => void
  title: string
  emptyTitle: string
  emptyHint: string
  editorDescription: string
  footnote?: React.ReactNode
}) {
  const { can } = useAuth()
  /** 'new' drafts a plan; a plan is the draft being rewritten. */
  const [editing, setEditing] = useState<'new' | CommissionPlan | null>(null)

  const draft = plans.find((plan) => plan.status === 'Draft')
  const active = plans.find((plan) => plan.status === 'Active')
  const past = plans.filter((plan) => plan.status === 'Superseded')

  return (
    <>
      {active && (
        <div className="mb-5 rounded-md border border-accent/20 bg-accent-wash px-4 py-3 text-[13px] text-accent">
          <b>In force since {date(active.effectiveFrom)}.</b> These rates are copied onto a
          quotation when it is submitted. Editing a plan never changes a deal already issued.
        </div>
      )}

      {footnote}

      {draft && (
        <PlanCard
          plan={draft}
          subject={subject}
          title={title}
          onChanged={onChanged}
          onEdit={() => setEditing(draft)}
        />
      )}
      {active && <PlanCard plan={active} subject={subject} title={title} onChanged={onChanged} />}

      {!draft && !active && (
        <Card className="mb-5">
          <EmptyState
            title={emptyTitle}
            hint={emptyHint}
            action={
              can('reseller.commission.plan.edit') ? (
                <Button variant="primary" onClick={() => setEditing('new')}>
                  Draft a plan
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}

      {!draft && active && can('reseller.commission.plan.edit') && (
        <div className="mb-5">
          <Button onClick={() => setEditing('new')}>Draft a replacement</Button>
        </div>
      )}

      {past.length > 0 && <PastPlans plans={past} subject={subject} />}

      {/*
        Mounted only while open, so it seeds from what is on screen now. Kept mounted, its form
        was seeded once from whichever tier and plan in force were showing the first time this
        rendered - and would offer that grid again after a tier switch or an activation.
      */}
      {editing !== null && (
        <PlanEditor
          subject={subject}
          countryId={countryId}
          description={editorDescription}
          draft={editing === 'new' ? undefined : editing}
          basedOn={active}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null)
            onChanged()
          }}
        />
      )}
    </>
  )
}

function PastPlans({ plans, subject }: { plans: readonly CommissionPlan[]; subject: PlanSubject }) {
  return (
    <Card>
      <CardHeader
        title="Previous plans"
        hint="Kept because issued quotations carry a copy of the grid that was in force when they were submitted."
      />
      <Table>
        <thead>
          <tr>
            <Th>Effective</Th>
            <Th>Until</Th>
            <Th>Year 1 one-time</Th>
            <Th>Year 1 recurring</Th>
            {allowsProductRows(subject) && <Th>Products</Th>}
            <Th>Activated</Th>
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => (
            <Tr key={plan.id}>
              <Td>{date(plan.effectiveFrom)}</Td>
              <Td>{plan.effectiveTo ? date(plan.effectiveTo) : <span className="text-faint">&mdash;</span>}</Td>
              <Td className="tabular">{blanketRateOf(plan.rates, 1, 'OneTime')}</Td>
              <Td className="tabular">{blanketRateOf(plan.rates, 1, 'Recurring')}</Td>
              {allowsProductRows(subject) && (
                <Td className="tabular">
                  {plan.products.length === 0 ? 'none' : plan.products.length}
                </Td>
              )}
              <Td>{plan.activatedAt ? dateTime(plan.activatedAt) : <span className="text-faint">&mdash;</span>}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

/** The blanket rate for one cell. What a product with no row of its own is paid. */
function blanketRateOf(
  rates: readonly CommissionCell[],
  year: number,
  kind: PaymentBasis,
): string {
  const cell = rates.find(
    (r) => r.contractYear === year && r.billingKind === kind && r.productId === null,
  )
  return cell ? `${cell.percent.toFixed(2)}%` : '—'
}

function PlanCard({
  plan,
  subject,
  title,
  onChanged,
  onEdit,
}: {
  plan: CommissionPlan
  subject: PlanSubject
  title: string
  onChanged: () => void
  /** A draft only. A plan in force is never edited - a change is a new version. */
  onEdit?: () => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const [activating, setActivating] = useState(false)

  const missing = missingRates(plan.rates).filter(
    (cell) => cell.productId !== null || !blanketIsOptional(subject) || hasBlanket(plan.rates),
  )

  async function activate() {
    setActivating(true)
    try {
      await api.post(`/commission-plans/${plan.id}/activate`)
      toast.show('Plan in force', 'success')
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not activate the plan', 'error')
    } finally {
      setActivating(false)
    }
  }

  return (
    <Card className="mb-5">
      <CardHeader
        title={`${title} — ${plan.status === 'Draft' ? 'draft' : 'in force'} from ${date(plan.effectiveFrom)}`}
        hint={plan.note ?? undefined}
        action={
          <div className="flex items-center gap-2">
            <Badge tone={plan.status === 'Draft' ? 'warning' : 'positive'}>{plan.status}</Badge>
            {plan.status === 'Draft' && onEdit && can('reseller.commission.plan.edit') && (
              <Button onClick={onEdit} disabled={activating}>
                Edit
              </Button>
            )}
            {plan.status === 'Draft' && can('reseller.commission.plan.activate') && (
              <Button
                variant="primary"
                onClick={activate}
                disabled={activating || missing.length > 0}
              >
                {activating ? 'Activating...' : 'Activate'}
              </Button>
            )}
          </div>
        }
      />

      <Table>
        <thead>
          <tr>
            <Th>Contract year</Th>
            <Th numeric>One-time %</Th>
            <Th numeric>Recurring %</Th>
          </tr>
        </thead>
        <tbody>
          {(hasBlanket(plan.rates) || !blanketIsOptional(subject)) && (
            <>
              <BandRow label={blanketLabel(subject)} />
              {CONTRACT_YEARS.map((year) => (
                <RateRow key={year} rates={plan.rates} year={year} productId={null} />
              ))}
            </>
          )}
          {plan.products.map((product) => (
            <ProductRows key={product.id} product={product} rates={plan.rates} />
          ))}
        </tbody>
      </Table>

      {missing.length > 0 && (
        <p className="border-t border-line-soft bg-crit-wash px-5 py-3 text-[13px] text-crit">
          {missing.length === 1 ? 'One cell has' : `${missing.length} cells have`} not been
          answered. Type 0.00 where nothing is earned &mdash; a plan cannot be activated with a
          cell nobody has decided, because a blank that silently means zero makes an entitlement
          depend on somebody having got bored halfway down a column.
        </p>
      )}
    </Card>
  )
}

function hasBlanket(rates: readonly CommissionCell[]): boolean {
  return rates.some((rate) => rate.productId === null)
}

function blanketLabel(subject: PlanSubject): string {
  return allowsProductRows(subject)
    ? 'All products — applies to anything with no row of its own'
    : 'This product'
}

function BandRow({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={3}
        className="border-t border-line bg-surface-2 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted"
      >
        {label}
      </td>
    </tr>
  )
}

function ProductRows({
  product,
  rates,
}: {
  product: CommissionPlanProduct
  rates: readonly CommissionCell[]
}) {
  return (
    <>
      <BandRow label={product.sku === '' ? product.name : `${product.name} · ${product.sku}`} />
      {CONTRACT_YEARS.map((year) => (
        <RateRow key={year} rates={rates} year={year} productId={product.id} />
      ))}
    </>
  )
}

function RateRow({
  rates,
  year,
  productId,
}: {
  rates: readonly CommissionCell[]
  year: number
  productId: string | null
}) {
  return (
    <Tr>
      <Td>Year {year}</Td>
      {BILLING_KINDS.map((kind) => {
        const cell = rates.find(
          (r) =>
            r.contractYear === year && r.billingKind === kind && (r.productId ?? null) === productId,
        )
        return (
          <Td key={kind} numeric className={cell === undefined ? 'text-crit' : undefined}>
            {cell === undefined ? 'not answered' : `${cell.percent.toFixed(2)}%`}
          </Td>
        )
      })}
    </Tr>
  )
}

function PlanEditor({
  subject,
  countryId,
  description,
  draft,
  basedOn,
  onClose,
  onDone,
}: {
  subject: PlanSubject
  countryId: string | undefined
  description: string
  /** The draft being rewritten. Absent, a new draft is saved instead. */
  draft?: CommissionPlan | undefined
  /** The plan in force, if any. A replacement starts from it rather than from blanks. */
  basedOn?: CommissionPlan | undefined
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const seed = draft ?? basedOn
  const [effectiveFrom, setEffectiveFrom] = useState(() => draft?.effectiveFrom.slice(0, 10) ?? '')
  const [note, setNote] = useState(() => draft?.note ?? '')
  const [saving, setSaving] = useState(false)
  const [cells, setCells] = useState<Record<string, string>>(() => seedGrid(seed?.rates))
  const [productIds, setProductIds] = useState<string[]>(() => productsOf(seed?.rates))
  const [blanket, setBlanket] = useState(
    () => !seed || !blanketIsOptional(subject) || hasBlanket(seed.rates),
  )
  const [adding, setAdding] = useState('')

  const products = allowsProductRows(subject)

  // Only where product rows are allowed. A standard grid is already about one product, so
  // fetching the catalogue for it would be a request nothing on the screen can use.
  const catalogue = useAsync<Paged<ProductListItem> | null>(
    (signal) =>
      products
        ? api.get<Paged<ProductListItem>>('/products', { pageSize: 200, sort: 'name' }, signal)
        : Promise.resolve(null),
    [products],
  )

  const catalogueItems = useMemo(() => catalogue.data?.items ?? [], [catalogue.data])
  // The draft's own labels underneath, for a product the first page of the catalogue misses.
  const named = useMemo(
    () =>
      new Map<string, { name: string }>([
        ...(draft?.products ?? []).map((product) => [product.id, product] as const),
        ...catalogueItems.map((product) => [product.id, product] as const),
      ]),
    [catalogueItems, draft],
  )

  function set(year: number, kind: PaymentBasis, productId: string | null, value: string) {
    setCells((current) => ({ ...current, [cellKey(year, kind, productId)]: value }))
  }

  function addProduct(productId: string) {
    if (productId === '' || productIds.includes(productId)) return
    setCells((current) => seedProduct(current, productId))
    setProductIds((current) => [...current, productId])
    setAdding('')
  }

  function removeProduct(productId: string) {
    setCells((current) => dropProduct(current, productId))
    setProductIds((current) => current.filter((id) => id !== productId))
  }

  // The blanket group is checked only when the plan carries one. Where it is optional and
  // switched off, its cells are simply not sent.
  const blanks = blanket
    ? missingCells(cells, productIds)
    : missingCells(cells, productIds).filter((cell) => cell.productId !== null)

  const empty = !blanket && productIds.length === 0

  async function save() {
    if (!countryId) return
    setSaving(true)
    try {
      const rates = gridToRates(cells, productIds).filter(
        (rate) => blanket || rate.productId !== null,
      )

      const body = {
        countryId,
        downlineResellerId: subject.kind === 'reseller' ? subject.downlineId : null,
        effectiveFrom,
        note: note.trim() === '' ? null : note.trim(),
        rates,
      }

      // A draft is rewritten in place. Nothing is in force on it, so there is no history to keep.
      if (draft) await api.put(`/commission-plans/${draft.id}`, body)
      else await api.post(listUrl(subject), body)

      toast.show('Draft saved', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save the plan', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      title={draft ? 'Edit draft commission plan' : 'Draft a commission plan'}
      description={description}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={saving || effectiveFrom === '' || blanks.length > 0 || empty || !countryId}
          >
            {saving ? 'Saving...' : 'Save draft'}
          </Button>
        </>
      }
    >
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <Field label="Effective from" required hint="A quotation submitted on or after this date freezes these rates.">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          )}
        </Field>
        <Field label="Note">
          {(id) => <Textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Contract year</Th>
            <Th numeric>One-time %</Th>
            <Th numeric>Recurring %</Th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={3}
              className="border-t border-line bg-surface-2 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted"
            >
              <span className="flex flex-wrap items-center gap-2">
                {blanketLabel(subject)}
                {blanketIsOptional(subject) && (
                  <label className="flex items-center gap-1.5 font-medium normal-case tracking-normal text-ink-2">
                    <input
                      type="checkbox"
                      checked={blanket}
                      onChange={(e) => setBlanket(e.target.checked)}
                    />
                    Override every standard rate
                  </label>
                )}
              </span>
            </td>
          </tr>
          {blanket &&
            CONTRACT_YEARS.map((year) => (
              <EditRow key={year} year={year} productId={null} cells={cells} onChange={set} />
            ))}

          {productIds.map((productId) => (
            <Fragment key={productId}>
              <tr>
                <td
                  colSpan={3}
                  className="border-t border-line bg-surface-2 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    {named.get(productId)?.name ?? 'Product'}
                    <button
                      type="button"
                      className="font-medium normal-case tracking-normal text-crit underline"
                      onClick={() => removeProduct(productId)}
                    >
                      Remove
                    </button>
                  </span>
                </td>
              </tr>
              {CONTRACT_YEARS.map((year) => (
                <EditRow
                  key={year}
                  year={year}
                  productId={productId}
                  cells={cells}
                  onChange={set}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </Table>

      {products && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select
            aria-label="Add a product exception"
            value={adding}
            onChange={(e) => addProduct(e.target.value)}
            className="max-w-xs"
          >
            <option value="">Add a product exception…</option>
            {catalogueItems
              .filter((product) => !productIds.includes(product.id))
              .map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
          </Select>
          <span className="text-[12px] text-muted">
            Anything not listed is paid that product&rsquo;s standard rate.
          </span>
        </div>
      )}

      {empty && (
        <p className="mt-3 rounded-md bg-crit-wash px-4 py-3 text-[13px] text-crit">
          This plan says nothing. Either override every standard rate, or add a product
          exception.
        </p>
      )}

      {blanks.length > 0 && (
        <p className="mt-3 rounded-md bg-crit-wash px-4 py-3 text-[13px] text-crit">
          Every cell needs an answer, including the zeroes. Zero means <i>not entitled</i>, and
          it is typed rather than assumed. A group is all ten of its cells or none at all.
        </p>
      )}
    </Dialog>
  )
}

function EditRow({
  year,
  productId,
  cells,
  onChange,
}: {
  year: number
  productId: string | null
  cells: Readonly<Record<string, string>>
  onChange: (year: number, kind: PaymentBasis, productId: string | null, value: string) => void
}) {
  return (
    <Tr>
      <Td>Year {year}</Td>
      {BILLING_KINDS.map((kind) => (
        <Td key={kind} numeric>
          <input
            aria-label={`Year ${year} ${kind === 'OneTime' ? 'one-time' : 'recurring'} percentage`}
            className={`qh-field tabular w-24 rounded-sm border bg-surface px-2 text-right text-[13px] ${
              (cells[cellKey(year, kind, productId)] ?? '').trim() === ''
                ? 'border-crit bg-crit-wash'
                : 'border-line'
            }`}
            inputMode="decimal"
            value={cells[cellKey(year, kind, productId)] ?? ''}
            onChange={(e) => onChange(year, kind, productId, e.target.value)}
          />
        </Td>
      ))}
    </Tr>
  )
}

/** Shared by both callers: fetch the subject's plans, then hand them to the grid. */
export function useCommissionPlans(subject: PlanSubject) {
  const url = listUrl(subject)

  return useAsync<CommissionPlan[]>(
    (signal) => api.get<CommissionPlan[]>(url, undefined, signal),
    [url],
  )
}

export { ErrorState as CommissionErrorState, Loading as CommissionLoading }
